import {useSaveDeezerStoryQuizScoreMutation} from '@graphql/generated';
import type {ReactNode} from 'react';
import React, {useContext, useState, useCallback, useEffect} from 'react';

interface ScoreTier {
	maxTime: number;
	points: number;
}

interface GameContextType {
	// Score
	score: number;
	lastPointsEarned: number;
	updateScore: (timeTaken: number, isCorrect: boolean) => number;

	// Score Sending
	isSending: boolean;
	sendError: string | null;
	sendScoreToBackend: () => Promise<any>;

	// User Management
	userName: string;
	setUserName: (name: string) => void;

	// Authentication
	token: string;
	setToken: (token: string) => void;
}

export const QUESTIONS_LENGTH = 8;
export const TOTAL_TIME = 15;
export const SCORE_TIERS: ScoreTier[] = [
	{maxTime: 5, points: 125},
	{maxTime: 10, points: 50},
	{maxTime: 15, points: 25},
];

const GameContext = React.createContext<GameContextType | undefined>(undefined);

export const ReceiverGameProvider: React.FC<{children: ReactNode}> = ({
	children,
}) => {
	const [saveScore, {loading: isSending, error: sendError}] =
		useSaveDeezerStoryQuizScoreMutation();

	// Score State
	const [score, setScore] = useState<number>(0);
	const [lastPointsEarned, setLastPointsEarned] = useState<number>(0);

	// User Management State
	const [userName, setUserName] = useState<string>('');

	// Token (from campaign url)
	const [token, setToken] = useState<string>('');

	// Score Methods
	const calculatePoints = useCallback(
		(timeTaken: number, isCorrect: boolean) => {
			if (!isCorrect) return 0;
			return SCORE_TIERS.find((tier) => timeTaken <= tier.maxTime)?.points || 0;
		},
		[],
	);

	const updateScore = useCallback(
		(timeTaken: number, isCorrect: boolean) => {
			const pointsEarned = calculatePoints(timeTaken, isCorrect);
			setScore((prevScore) => prevScore + pointsEarned);
			setLastPointsEarned(pointsEarned);

			return pointsEarned;
		},
		[calculatePoints],
	);

	// Score Sending Method
	const sendScoreToBackend = useCallback(async () => {
		saveScore({variables: {playerName: userName, token, score}});
	}, [score, token, userName, saveScore]);

	const value = {
		// Score
		score,
		lastPointsEarned,
		updateScore,

		// Score Sending
		isSending,
		sendError: (sendError && sendError.message) || null,
		sendScoreToBackend,

		// User Management
		userName,
		setUserName,

		// Authentication
		token,
		setToken,
	};

	return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};

// Hook to use the game context
export const useGame = () => {
	const context = useContext(GameContext);
	if (context === undefined) {
		throw new Error('useGame must be used within a GameProvider');
	}
	return context;
};

export const useGameTimer = (initialTime: number = TOTAL_TIME) => {
	const [timeLeft, setTimeLeft] = useState<number>(initialTime);
	const [isRunning, setIsRunning] = useState<boolean>(false);

	const stopTimer = useCallback(() => {
		setIsRunning(false);
	}, []);

	const startTimer = useCallback(() => {
		setTimeLeft(initialTime); // Reset timer when starting
		setIsRunning(true);
	}, [initialTime]);

	const resetTimer = useCallback((newTime: number = TOTAL_TIME) => {
		setTimeLeft(newTime);
		setIsRunning(true);
	}, []);

	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (isRunning && timeLeft > 0) {
			timer = setInterval(() => {
				setTimeLeft((prev) => prev - 1);
			}, 1000);
		}

		return () => {
			clearInterval(timer);
		};
	}, [isRunning, timeLeft, initialTime]);

	return {timeLeft, isRunning, stopTimer, startTimer, resetTimer};
};

export const useGameScore = () => {
	const {score, lastPointsEarned, updateScore} = useGame();
	return {score, lastPointsEarned, updateScore};
};

export const useSendScore = () => {
	const {isSending, sendError, sendScoreToBackend} = useGame();
	return {isSending, sendError, sendScoreToBackend};
};

export const useUserName = () => {
	const {userName, setUserName} = useGame();
	return {userName, setUserName};
};

export const useToken = () => {
	const {token, setToken} = useGame();
	return {token, setToken};
};
